<script setup>
import {ErrorMessage, Field, Form} from 'vee-validate'
import {reactive, ref} from 'vue'
import axios from 'axios'
import cookieHelper from '../cookie'
import useGlobalStore from '../../store/globalStore'
import {useFormSubmit} from '../../composables/useFormSubmit'

const props = defineProps({
  listOfLevelOfCare: {
    type: Array,
    default: [],
  },
  listRespiteTypes: {
    type: Array,
    default: [],
  },
})

const schema = {
  first_name: 'required',
  last_name: 'required',
  email: 'required|email',
  phone_number: 'required|phoneNumber',
  levelOfCare: 'required',
}

const careHomeLevelOfCare = ref('')
const respiteCareType = ref('Rest home level care')
let isSubmitting = ref(false)
const confirmation = reactive({show: false, message: '', class: ''})
const globalStore = useGlobalStore()
const { hideModal, showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('listEnquiryForm');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = response.data.msg
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      let currentUrl = window.location.href;
      currentUrl = currentUrl.replace('#enquiry-submitted', '');
      history.pushState(null, null, `${currentUrl}#enquiry-submitted`);

      const modalTitle = globalStore.modal.title;
      if (cookieHelper.getCookie('careHomeSuccessInfo') === null) {
        globalStore.setModal(
          {
            name: 'careHomeEnquirySuccess',
            title: modalTitle,
            id: response.data.id
          }
        )
      } else {
        hideModal()
      }
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}

</script>

<template>
  <Form
    class="modal-form modal-form--2column"
    :validation-schema="schema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="listEnquiryForm"
  >
    <slot name="token"></slot>
    <div class="w240 color-pickled font-lato font-italic font-weight-bold fs-14 mt-3 ml-auto mr-auto lh1-4">
      Check current or up-coming availability. A response will be sent to you by email
    </div>
    <div class="mt-5">
      <div class="row">
        <div class="col-md-6 pr-md-1">
          <div class="form-group form-group--input">
            <label for="name" class="sr-only">First name</label>
            <Field
              id="first_name"
              name="first_name"
              placeholder="First name *"
              class="form-control"
              :class="{'is-danger': errors['first_name'] }"
              tabindex="1"
              autofocus
              autocomplete="off"
            />
            <ErrorMessage name="first_name" class="is-danger" />
          </div>
        </div>
        <div class="col-md-6 pl-md-1">
          <div class="form-group form-group--input">
            <label for="last_name" class="sr-only">Last name</label>
            <Field
              name="last_name"
              placeholder="Last name *"
              class="form-control"
              :class="{'is-danger': errors['last_name'] }"
              tabindex="2"
              id="last_name"
            />
            <ErrorMessage name="last_name" class="is-danger" />
          </div>
        </div>
      </div>

      <div class="form-group form-group--input">
        <label for="email" class="sr-only">Email</label>
        <Field
          tabindex="3"
          id="email"
          name="email"
          placeholder="Email *"
          class="form-control"
          :class="{'is-danger': errors['email'] }"
        />
        <ErrorMessage name="email" class="is-danger" />
      </div>

      <div class="form-group form-group--input">
        <label for="phone_number" class="sr-only">Phone</label>
        <Field
          tabindex="4"
          id="phone_number"
          name="phone_number"
          placeholder="Phone *"
          class="form-control"
          :class="{'is-danger': errors['phone_number'] }"
        />
        <ErrorMessage name="phone_number" class="is-danger" />
      </div>
    </div>
    <p class="fs-12 font-weight-bold color-pickled mt-1">
      Has a needs assessment been completed? <span class="text-red">*</span>
    </p>
    <div class="form-group form-group--input">
      <div class="radio-custom radio-custom--inline">
        <input type="radio" id="yes" name="assessment" value="Yes" checked tabindex="5">
        <label class="form-check-label fs-14 font-weight-light-bold" for="yes">Yes</label>
      </div>
      <div class="radio-custom radio-custom--inline">
        <input type="radio" id="in_progress" name="assessment" value="In progress">
        <label class="form-check-label fs-14 font-weight-light-bold" for="in_progress">It's in progress</label>
      </div>
      <div class="radio-custom radio-custom--inline">
        <input type="radio" id="No" name="assessment" value="No">
        <label class="form-check-label fs-14 font-weight-light-bold" for="No">No</label>
      </div>
    </div>
    <div class="form-group form-group--input">
      <label class="fs-12 font-weight-bold color-pickled d-block mt-3">
        What level of care is needed? <span class="text-red">*</span>
      </label>
      <Field
        name="levelOfCare"
        as="select"
        class="form-control form-control--select form-control--select-45 font-weight-bold color-pickled"
        aria-required="true"
        aria-invalid="false"
        v-model="careHomeLevelOfCare"
        tabindex="6"
      >
        <option :value="item.value" v-for="item in props.listOfLevelOfCare" :key="item.name" v-html="item.name"></option>
      </Field>
      <ErrorMessage name="levelOfCare" class="is-danger" as="span">
        The level of care field is required
      </ErrorMessage>
    </div>
    <div class="form-group form-group--input" v-if="careHomeLevelOfCare === 'Respite care'">
      <label class="fs-12 font-weight-bold color-pickled d-block mt-3">
        What type of respite care is required? <span class="text-red">*</span>
      </label>
      <Field
        name="respiteCareType"
        as="select"
        class="form-control form-control--select form-control--select-45 font-weight-bold color-pickled"
        aria-required="true"
        aria-invalid="false"
        v-model="respiteCareType"
      >
        <option :value="item.value" v-for="item in props.listRespiteTypes" :key="item.name" v-html="item.name"></option>
      </Field>
      <ErrorMessage name="levelOfCare" class="is-danger" >The respite type field is required</ErrorMessage>
    </div>
    <div class="form-group">
      <label for="message" class="sr-only">Message</label>
      <Field
        as="textarea"
        tabindex="9"
        id="message"
        rows="3"
        placeholder="Message"
        class="form-control form-control--textarea pl-2 p-2 pt-3 pb-3"
        name="message"
      ></Field>
    </div>
    <transition name="fade">
      <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
    </transition>

    <Field type="hidden" name="captcha_action" value="care_home_enquiry" />
    <slot name="hiddenfields"></slot>
    <button
      :disabled="isSubmitting"
      tabindex="12"
      type="submit"
      class="btn btn--green submit mt-4 fs-14 font-weight-bold pt-2 pb-2 w-100"
    >
      Check availability
    </button>
    <slot name="googlerecaptchatext"></slot>
  </Form>
</template>
